import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Password} from 'primereact/password';
import {Messages} from 'primereact/messages';
import {Captcha} from 'primereact/captcha';

import {AuthService} from "../../service/AuthService";
import {UnAuthService} from "../../service/UnAuthService";

import queryString from "query-string";

import {AppContext} from "../../HomePage";
import {getKeyValue, emailValidator} from "../../utils/Utils";
import SocialLoginButtons from "./SocialLoginButtons";
import AuthLayout from "./AuthLayout";
import AuthField from "./AuthField";
import TwoFactorChallenge from "./TwoFactorChallenge";

const Login  = (props) =>  {

    const {t, host, appName, navigate, location, setError} = React.useContext(AppContext);

    const messages = useRef(null);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);

    const [email, setEmail] = useState(localStorage.getItem("loggedInEmail") || '');
    const [password, setPassword] = useState('');
    const [fieldErrors, setFieldErrors] = useState({email: '', password: ''});
    const [submitted, setSubmitted] = useState(false);
    const [formError, setFormError] = useState('');
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [captchaValid, setCaptchaValid] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [challenge, setChallenge] = useState(null);

    useEffect(() => {
        if (location !== undefined)
        {
            let params = queryString.parse(location.search);
            if (params)
            {
                if(params.register && params.register === "1")
                    messages.current.show({sticky: true, severity: "success", summary: "", detail: t('login.await_activation_detail_1', {appName: appName})});
                else if (params.recover && params.recover === "1")
                    messages.current.show({sticky: true, severity: "success", summary: "", detail: t('login.recovery_mail_detail')});
                else if (params.recover && params.recover === "2")
                    messages.current.show({sticky: true, severity: "success", summary: "", detail: t('login.password_changed_detail')});
                else if (params.expire && params.expire === "1")
                    messages.current.show({sticky: true, severity: "warn", summary: "", detail: t('login.token_expire')});

            }
        }
        if(AuthService.loggedIn()) {
            navigate("/");
        }

        setShowCaptcha(localStorage.getItem("loginRetry") && localStorage.getItem("loginRetry") === "true");

        // Focus the field the user still needs to fill in: the password when
        // the email arrives prefilled from a previous session, the email
        // field otherwise.
        if (localStorage.getItem("loggedInEmail")) {
            passwordRef.current && passwordRef.current.focus();
        } else {
            emailRef.current && emailRef.current.focus();
        }
    }, [])

    const validateEmail = (value) => value.trim() === '' ? t('login.email_required') : emailValidator(value.trim(), t);
    const validatePassword = (value) => value === '' ? t('login.password_required') : '';

    const signup = () => {
        navigate("/register");
    };

    const handleLoginSuccess = () => {
        localStorage.removeItem("loginRetry");
        setShowCaptcha((localStorage.getItem("loginRetry") && localStorage.getItem("loginRetry") === "true"));
        navigate("/campaign");
    };

    const validate = () => {
        const errors = {
            email: validateEmail(email),
            password: validatePassword(password)
        };
        setFieldErrors(errors);
        return errors.email === '' && errors.password === '';
    };

    const login = (event) => {

        if (event)
            event.preventDefault();

        if (disabled)
            return;

        setFormError('');
        setSubmitted(true);

        if (!validate())
            return;

        if (showCaptcha && !captchaValid) {
            setFormError(t('login.captcha_detail'));
            return;
        }

        setDisabled(true);

        UnAuthService.login(email.trim(), password).then((res) => {
            if (res.data && res.data.two_factor_required) {
                setChallenge(res.data);
                setDisabled(false);
                return;
            }
            handleLoginSuccess();
        }).catch(error => {
            const data = error.response && error.response.data;
            const translated = data && data.error_key ? t(data.error_key) : null;
            if (translated && translated !== data.error_key) {
                setFormError(translated);
            } else {
                setError(error);
                setError(null);
            }
            setDisabled(false);
            setShowCaptcha((localStorage.getItem("loginRetry") && localStorage.getItem("loginRetry") === "true"));
            localStorage.setItem("loginRetry", "true");
        });
    };

    const recover = () => {
        navigate("/recovery");
    };

    const captchaResponse = () => {
        setCaptchaValid(true);
    };

    if (challenge)
        return <TwoFactorChallenge challenge={challenge}
                                   onSuccess={handleLoginSuccess}
                                   onCancel={() => { setChallenge(null); setPassword(''); }}/>;

    return <AuthLayout title={t('login.login_title')} subtitle={t('login.login_subtitle')}
                       footer={host !== "ecomscanner" &&
                           <span>{t('login.sign_up_message_1')}
                               <button type="button" className="auth-ux__link" onClick={signup}>{t('actions.join_us')}</button>
                               {t('login.sign_up_message_2')}</span>}>

        <Messages ref={messages}/>

        <form onSubmit={login} noValidate>

            {formError && <div className="auth-ux__form-error" role="alert">{formError}</div>}

            <AuthField id="email" label={t('user.email')} error={fieldErrors.email}>
                <InputText name="email" type="email" inputMode="email" autoComplete="username" ref={emailRef}
                           value={email} onChange={(e) => {
                               const value = getKeyValue(e).value;
                               setEmail(value);
                               if (submitted)
                                   setFieldErrors(prev => ({...prev, email: validateEmail(value)}));
                           }}/>
            </AuthField>

            <AuthField id="password" label={t('user.password')} error={fieldErrors.password}>
                <Password name="password" feedback={false} toggleMask={true} autoComplete="current-password" inputRef={passwordRef}
                          value={password} onChange={(e) => {
                              const value = getKeyValue(e).value;
                              setPassword(value);
                              if (submitted)
                                  setFieldErrors(prev => ({...prev, password: validatePassword(value)}));
                          }}/>
            </AuthField>

            <div className="auth-ux__inline-actions">
                <button type="button" className="auth-ux__link" onClick={recover}>{t('user.forgot_password')}</button>
            </div>

            {showCaptcha &&
            <div className="auth-ux__field">
                <Captcha siteKey="6LfwsuYUAAAAAHfOxoAxTJYehxxhfLO_SSJZDaaL" onResponse={captchaResponse}/>
            </div>}

            <Button type="submit" label={t('actions.login')} icon="pi pi-sign-in"
                    className="auth-ux__submit" disabled={disabled} loading={disabled}/>
        </form>

        <div className="auth-ux__divider"><span>{t('login.social_separator')}</span></div>

        <SocialLoginButtons t={t} messages={messages} setError={setError} onSuccess={handleLoginSuccess} disabled={disabled}/>
    </AuthLayout>;
};
export default Login;
